import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AddOccasionModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: 'birthday',
    date: '',
    recipients: [''],
    budget: '',
    notes: '',
    reminders: {
      twoWeeks: false,
      oneWeek: false,
      threeDays: false,
      oneDay: false
    }
  });

  const categories = [
    { value: 'birthday', label: 'Birthday', icon: 'Cake' },
    { value: 'anniversary', label: 'Anniversary', icon: 'Heart' },
    { value: 'holiday', label: 'Holiday', icon: 'Star' },
    { value: 'graduation', label: 'Graduation', icon: 'GraduationCap' },
    { value: 'wedding', label: 'Wedding', icon: 'Rings' },
    { value: 'baby', label: 'Baby Shower', icon: 'Baby' },
    { value: 'retirement', label: 'Retirement', icon: 'Briefcase' },
    { value: 'housewarming', label: 'Housewarming', icon: 'Home' },
    { value: 'other', label: 'Other', icon: 'Calendar' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRecipientChange = (index, value) => {
    const newRecipients = [...formData.recipients];
    newRecipients[index] = value;
    setFormData(prev => ({
      ...prev,
      recipients: newRecipients
    }));
  };

  const addRecipient = () => {
    setFormData(prev => ({
      ...prev,
      recipients: [...prev.recipients, '']
    }));
  };

  const removeRecipient = (index) => {
    if (formData.recipients.length > 1) {
      const newRecipients = formData.recipients.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        recipients: newRecipients
      }));
    }
  };

  const handleReminderChange = (reminder, checked) => {
    setFormData(prev => ({
      ...prev,
      reminders: {
        ...prev.reminders,
        [reminder]: checked
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const occasionData = {
      ...formData,
      recipients: formData.recipients.filter(r => r.trim() !== ''),
      giftStatus: 'not_started',
      id: Date.now() // Simple ID generation for demo
    };
    
    onSave(occasionData);
    
    // Reset form
    setFormData({
      title: '',
      category: 'birthday',
      date: '',
      recipients: [''],
      budget: '',
      notes: '',
      reminders: {
        twoWeeks: false,
        oneWeek: false,
        threeDays: false,
        oneDay: false
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-accent-100">
          <h2 className="text-2xl font-heading font-semibold text-accent-800">
            Add New Occasion
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            className="text-accent-500 hover:text-accent-700"
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Occasion Title *
            </label>
            <Input
              type="text"
              placeholder="e.g., Mom's Birthday, Wedding Anniversary"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              required
              className="w-full"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Category *
            </label>
            <div className="grid grid-cols-3 gap-3">
              {categories.map((category) => (
                <button
                  key={category.value}
                  type="button"
                  onClick={() => handleInputChange('category', category.value)}
                  className={`flex items-center space-x-2 p-3 rounded-xl border-2 transition-all duration-200 ${
                    formData.category === category.value
                      ? 'border-primary-300 bg-primary-50 text-primary-700' :'border-accent-200 hover:border-accent-300 text-accent-600'
                  }`}
                >
                  <Icon name={category.icon} size={18} />
                  <span className="text-sm font-medium">{category.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Date *
            </label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              required
              className="w-full"
            />
          </div>

          {/* Recipients */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Recipients
            </label>
            <div className="space-y-3">
              {formData.recipients.map((recipient, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Input
                    type="text"
                    placeholder="Recipient name"
                    value={recipient}
                    onChange={(e) => handleRecipientChange(index, e.target.value)}
                    className="flex-1"
                  />
                  {formData.recipients.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      onClick={() => removeRecipient(index)}
                      className="text-error-500 hover:text-error-700"
                    />
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                onClick={addRecipient}
                className="text-accent-600"
              >
                Add Recipient
              </Button>
            </div>
          </div>

          {/* Budget */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Budget (Optional)
            </label>
            <Input
              type="number"
              placeholder="Enter budget amount"
              value={formData.budget}
              onChange={(e) => handleInputChange('budget', e.target.value)}
              className="w-full"
            />
          </div>

          {/* Reminders */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-3">
              Reminder Settings
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'twoWeeks', label: '2 weeks before' },
                { key: 'oneWeek', label: '1 week before' },
                { key: 'threeDays', label: '3 days before' },
                { key: 'oneDay', label: '1 day before' }
              ].map((reminder) => (
                <label key={reminder.key} className="flex items-center space-x-2 cursor-pointer">
                  <Input
                    type="checkbox"
                    checked={formData.reminders[reminder.key]}
                    onChange={(e) => handleReminderChange(reminder.key, e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-accent-700">{reminder.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              placeholder="Add any special notes or gift ideas..."
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-accent-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-accent-100">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="text-accent-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              iconName="Plus"
              iconPosition="left"
              className="shadow-warm"
            >
              Add Occasion
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOccasionModal;